import { Pencil, User, X } from 'lucide-react'
import { useState } from 'react'
import { apiErrorMessage } from '../../api/client'
import { profileApi } from '../../api/endpoints'
import { ErrorMessage } from '../ErrorMessage'
import type { ProfileResponse } from '../../types/api'

/**
 * A tutor's own words about themselves - bio, qualifications, experience. None of these are
 * derived: NeedProjection never touches them, so unlike serviceType there is nothing here a
 * service change could silently overwrite, which is exactly why this can be a plain editor where
 * {@link RegistrationDetails} deliberately is not one.
 *
 * <p>Fee range is deliberately not here: it is per service (see ServiceForm's own fee band), and a
 * second, general one on the profile would just be a second place for the same fact to disagree
 * with itself the moment a tutor charges differently for two subjects.
 */
export function AboutSection({ profile, onSaved }: { profile: ProfileResponse; onSaved: () => void }) {
  const [editing, setEditing] = useState(false)

  const hasAbout = !!(profile.bio || profile.qualificationsText || profile.yearsOfExperience != null)

  if (editing) {
    return <AboutForm profile={profile} onCancel={() => setEditing(false)} onSaved={() => { setEditing(false); onSaved() }} />
  }

  return (
    <div className="card-pad">
      <div className="flex items-start justify-between gap-3">
        <h2 className="flex items-center gap-2 font-bold text-ink-900">
          <User size={16} className="text-sun-500" /> About you
        </h2>
        <button
          type="button"
          onClick={() => setEditing(true)}
          aria-label="Edit about you"
          className="grid h-8 w-8 shrink-0 place-items-center rounded-full border border-ink-200 hover:bg-ink-100"
        >
          <Pencil size={14} />
        </button>
      </div>

      {hasAbout ? (
        <div className="mt-3 space-y-3">
          {profile.bio && <p className="text-sm text-ink-700">{profile.bio}</p>}
          <dl className="grid grid-cols-1 gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
            {profile.qualificationsText && (
              <div><dt className="text-ink-500">Qualifications</dt><dd className="font-semibold text-ink-900">{profile.qualificationsText}</dd></div>
            )}
            {profile.yearsOfExperience != null && (
              <div><dt className="text-ink-500">Experience</dt><dd className="font-semibold text-ink-900">{profile.yearsOfExperience} yrs</dd></div>
            )}
          </dl>
        </div>
      ) : (
        <p className="mt-2 text-sm text-ink-500">
          You have not added a bio yet. A short description helps families decide to reach out.
        </p>
      )}
    </div>
  )
}

function AboutForm({
  profile,
  onCancel,
  onSaved,
}: {
  profile: ProfileResponse
  onCancel: () => void
  onSaved: () => void
}) {
  const [bio, setBio] = useState(profile.bio ?? '')
  const [qualificationsText, setQualificationsText] = useState(profile.qualificationsText ?? '')
  const [yearsOfExperience, setYearsOfExperience] = useState(
    profile.yearsOfExperience != null ? String(profile.yearsOfExperience) : '',
  )
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  async function save() {
    setError(null)
    const years = yearsOfExperience.trim() ? Number(yearsOfExperience) : undefined
    if (yearsOfExperience.trim() && (!Number.isFinite(years) || years! < 0)) {
      setError('Years of experience must be a positive number')
      return
    }
    setSaving(true)
    try {
      await profileApi.updateOwn({
        bio: bio || undefined,
        qualificationsText: qualificationsText || undefined,
        yearsOfExperience: years,
      })
      onSaved()
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not save this'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="card-pad">
      <div className="flex items-start justify-between gap-3">
        <h2 className="flex items-center gap-2 font-bold text-ink-900">
          <User size={16} className="text-sun-500" /> About you
        </h2>
        <button type="button" onClick={onCancel} aria-label="Close" className="text-ink-400 hover:text-ink-700">
          <X size={17} />
        </button>
      </div>

      <div className="mt-3 space-y-3">
        <label className="block">
          <span className="label">Bio</span>
          <textarea value={bio} onChange={(e) => setBio(e.target.value)} className="input" rows={3} placeholder="8 years teaching Maths..." />
          <span className="mt-1 block text-xs text-ink-400">A clear picture of your teaching. Nothing personal.</span>
        </label>
        <label className="block">
          <span className="label">Qualifications</span>
          <input value={qualificationsText} onChange={(e) => setQualificationsText(e.target.value)} className="input" placeholder="M.Sc Physics, B.Ed" />
        </label>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <label className="block">
            <span className="label">Years of experience</span>
            <input
              type="number"
              min={0}
              value={yearsOfExperience}
              onChange={(e) => setYearsOfExperience(e.target.value)}
              className="input"
            />
          </label>
        </div>

        <ErrorMessage message={error} />

        <div className="flex justify-end gap-2 pt-1">
          <button type="button" onClick={onCancel} className="btn-ghost">Cancel</button>
          <button type="button" onClick={save} disabled={saving} className="btn-sun">
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  )
}
